import type { HighchartsReactRefObject } from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { z } from 'zod';
import type { graphTypeEnum } from '../_models/project';

type Legend = {
  name: string;
  color?: string;
  id: string | null;
  enableDrillDown: boolean;
  onClick: () => void;
};

type UseChartVisibilityProps = {
  highchartsRef: RefObject<HighchartsReactRefObject | null>;
  legendData: Legend[];
  graphType: z.infer<typeof graphTypeEnum>;
  /**
   * セッションストレージで状態を保持する際のキー。
   * 同一画面内で複数の Visualizer が共存しても状態が混ざらないよう、画面ごとに別名を渡す。
   */
  storageKey?: string;
  /**
   * 状態復元判定のために監視するクエリ。値が変わると非表示状態をリセットする。
   * デフォルトは空オブジェクト（クエリ非依存）。
   */
  searchQueryStates?: Record<string, unknown>;
};

type ChartVisibilityState = {
  graphType: string;
  hiddenNodes: string[];
};

type ChartPoint = {
  name: string;
  visible: boolean;
  options: {
    custom?: {
      id?: string;
    };
  };
  setVisible: (visible: boolean, redraw?: boolean) => void;
};

type ChartSeries = {
  points?: ChartPoint[];
  visible: boolean;
  options: {
    custom?: {
      id?: string;
    };
  };
  setVisible: (visible: boolean, redraw?: boolean) => void;
};

/**
 * グラフの表示/非表示状態を管理するカスタムフック
 * グラフタイプに依存しない統一されたインターフェースを提供
 */
export const useChartVisibility = ({
  highchartsRef,
  legendData,
  graphType,
  storageKey = 'analysis-chart-state',
  searchQueryStates,
}: UseChartVisibilityProps) => {
  const prevQuery = useRef<string>('');

  // セッションストレージから状態を復元（graphTypeが一致する場合のみ）
  const [hiddenNodes, setHiddenNodes] = useState<string[]>([]);

  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey);
    // searchQueryStates 未指定だと JSON.stringify(undefined) が undefined になるため、
    // 比較用に空オブジェクトへ正規化する
    const currentQuery = JSON.stringify(searchQueryStates ?? {});
    if (stored) {
      const state: ChartVisibilityState = JSON.parse(stored);
      // 保存されているgraphTypeと現在のgraphTypeが一致しない場合 = グラフの種類の切り替え時のみ復元
      setHiddenNodes(
        state.graphType !== graphType && currentQuery !== prevQuery.current
          ? state.hiddenNodes
          : [],
      );
    }
    prevQuery.current = currentQuery;
    sessionStorage.removeItem(storageKey);
  }, [graphType, searchQueryStates, storageKey]);

  useEffect(() => {
    return () => {
      const state: ChartVisibilityState = {
        graphType,
        hiddenNodes,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    };
  }, [hiddenNodes, graphType, storageKey]);

  const chart = highchartsRef.current?.chart;
  const series = (
    chart && 'series' in chart ? chart.series : []
  ) as ChartSeries[];

  const isPieOrTreeMapGraph =
    graphType === 'pieChart' || graphType === 'treeMap';

  /**
   * レジェンドのIDを統一的に取得
   * レジェンドデータのIDまたは名前を一貫した識別子として使用
   */
  const getLegendIdentifier = useCallback((legend: Legend): string => {
    return legend.id ?? legend.name;
  }, []);

  /**
   * チャートポイントに対応するレジェンドを検索
   * グラフタイプによって異なるID体系を吸収
   */
  const findMatchingLegend = useCallback(
    (point: ChartPoint): Legend | undefined => {
      return legendData.find(
        (legend) =>
          legend.name === point.name ||
          (legend.id &&
            (legend.id === point.options.custom?.id ||
              legend.id === point.name)),
      );
    },
    [legendData],
  );

  /**
   * チャートの全アイテムを表示状態にリセット
   */
  const resetAllVisible = useCallback(
    (series: ChartSeries[]) => {
      if (isPieOrTreeMapGraph && series[0]?.points) {
        // 円グラフ・ツリーマップの場合
        for (const point of series[0].points) {
          point.setVisible(true, false);
        }
      } else {
        // その他のグラフの場合
        for (const s of series) {
          s.setVisible(true, false);
        }
      }
    },
    [isPieOrTreeMapGraph],
  );

  /**
   * 非表示にすべきアイテムを特定
   */
  const getHiddenItems = useCallback(
    (series: ChartSeries[]): (ChartPoint | ChartSeries)[] => {
      if (isPieOrTreeMapGraph && series[0]?.points) {
        // 円グラフ・ツリーマップの場合
        return series[0].points.filter((point) => {
          const matchingLegend = findMatchingLegend(point);

          if (matchingLegend) {
            const legendIdentifier = getLegendIdentifier(matchingLegend);
            return hiddenNodes.includes(legendIdentifier);
          }

          // レジェンドデータに一致するものがない場合
          const pointId = point.options.custom?.id ?? point.name;
          return (
            hiddenNodes.includes(pointId) || hiddenNodes.includes(point.name)
          );
        });
      }

      // その他のグラフタイプの場合
      // custom.id が null になり得る軸（KS の project / fund-owner など）でも
      // 表示状態を保持できるよう、name フォールバックでマッチさせる
      return series.filter((s) => {
        const id = s.options.custom?.id;
        if (id) {
          return hiddenNodes.includes(id);
        }
        const name = (s as ChartSeries & { name?: string }).name;
        return !!name && hiddenNodes.includes(name);
      });
    },
    [isPieOrTreeMapGraph, findMatchingLegend, getLegendIdentifier, hiddenNodes],
  );

  /**
   * チャートの表示状態を更新
   */
  useEffect(() => {
    if (!chart || !series.length) {
      return;
    }

    // チャートの準備ができていない場合は何もしない
    if (
      isPieOrTreeMapGraph &&
      (!series[0] || !series[0].points || series[0].points.length === 0)
    ) {
      return;
    }

    // タイムアウトを設定して、チャートの描画が完了してから処理を実行
    const timeout = setTimeout(() => {
      // まず全ての項目を表示状態にリセット
      resetAllVisible(series);

      // 非表示にするアイテムを特定して非表示に設定
      const hiddenItems = getHiddenItems(series);
      if (hiddenItems.length) {
        for (const item of hiddenItems) {
          item.setVisible(false, false);
        }
      }

      // 最後に一度だけredrawを実行
      if (chart.redraw) {
        chart.redraw();
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [chart, series, isPieOrTreeMapGraph, resetAllVisible, getHiddenItems]);

  /**
   * アイテムの表示/非表示を切り替え
   */
  const toggleVisibility = useCallback(
    (legendIdentifier: string) => {
      const selectedItem =
        isPieOrTreeMapGraph && series[0]
          ? series[0].points?.find((point) => {
              const matchingLegend = findMatchingLegend(point);
              return (
                matchingLegend &&
                getLegendIdentifier(matchingLegend) === legendIdentifier
              );
            })
          : series.find(
              (s) =>
                (s.options.custom?.id ?? (s as any).name) === legendIdentifier,
            );

      if (selectedItem) {
        const visible = !selectedItem.visible;
        if (visible) {
          setHiddenNodes((prev) =>
            prev.filter((id) => id !== legendIdentifier),
          );
        } else {
          setHiddenNodes((prev) => [...prev, legendIdentifier]);
        }
        selectedItem.setVisible(visible);
      }
    },
    [isPieOrTreeMapGraph, series, findMatchingLegend, getLegendIdentifier],
  );

  /**
   * アイテムが非表示かどうかを判定
   */
  const isHidden = useCallback(
    (legendIdentifier: string): boolean => {
      return hiddenNodes.includes(legendIdentifier);
    },
    [hiddenNodes],
  );

  return {
    hiddenNodes,
    toggleVisibility,
    isHidden,
    getLegendIdentifier,
  };
};
